import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import Intercom, {
  boot as intercomBoot,
  shutdown as intercomShutdown,
  update as intercomUpdate,
} from '@intercom/messenger-js-sdk';
import { Observable, Subscription, catchError, distinctUntilChanged, map, of, shareReplay, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BusinessProfile } from '../../_interfaces/business.interface';
import { UserProfile } from '../../_interfaces/user.interface';
import { AppSelectors } from '../../store/app/app.selectors';
import { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';
import { AuthApiService } from '../auth-api-service/auth-api.service';

/** Required for JWT-based identity verification (EU/AU workspaces use a region-specific host). */
const INTERCOM_API_BASE = 'https://api-iam.intercom.io';

export interface IntercomBootOptions {
  /** JWT identity verification token from your backend (`intercom_user_jwt`) */
  intercomUserJwt?: string;
  hideDefaultLauncher?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class IntercomService implements OnDestroy {
  private readonly store = inject(Store);
  readonly authApiService = inject(AuthApiService);
  private readonly subscriptions = new Subscription();

  private bootedUserId: number | null = null;

  // Caches the in-flight/completed JWT fetch per user id so that repeated
  // `userDetails` emissions for the *same* user (e.g. from multiple NGXS
  // actions/guards patching the same profile) never trigger duplicate
  // `auth/intercom` requests.
  private tokenRequestUserId: number | null = null;
  private tokenRequest$: Observable<{ token: string } | null> | null = null;

  private get booted(): boolean {
    return this.bootedUserId !== null;
  }

  constructor() {
    this.wireStoreLifecycle();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private get appId(): string {
    return environment.intercomAppId ?? '';
  }

  private get isConfigured(): boolean {
    return !!this.appId;
  }

  /**
   * Boot (or re-boot) Intercom for an authenticated user.
   * Called automatically when AppState userDetails becomes available.
   */
  private bootUser(user: UserProfile, business?: BusinessProfile | null, options: IntercomBootOptions = {}): void {
    if (!this.isConfigured) {
      return;
    }

    const settings: IntercomSettings = this.buildSettings(user, business, options);

    if (this.booted) {
      intercomBoot(settings);
    } else {
      Intercom(settings);
    }
    this.bootedUserId = user.id;
  }

  /**
   * Push updated user attributes and/or ping Intercom for new messages.
   * Called automatically on route changes and store updates.
   */
  private update(data?: {
    user?: UserProfile;
    business?: BusinessProfile | null;
    hideDefaultLauncher?: boolean;
  }): void {
    if (!this.isConfigured || !this.booted) {
      return;
    }

    if (!data) {
      intercomUpdate({});
      return;
    }

    const { user, business, hideDefaultLauncher } = data;
    intercomUpdate({
      ...(user ? this.buildUserArgs(user) : {}),
      ...(business ? { company: this.buildCompanyArgs(business) } : {}),
      ...(hideDefaultLauncher !== undefined ? { hide_default_launcher: hideDefaultLauncher } : {}),
    });
  }

  /** End the Intercom session — called automatically when userDetails clear / logout. */
  private shutdown(): void {
    if (!this.booted) {
      return;
    }
    intercomShutdown();
    this.bootedUserId = null;
  }

  /**
   * Boot/keep Intercom booted app-wide as soon as a user is logged in, and keep it
   * present regardless of navigation. Shuts down on logout.
   */
  private wireStoreLifecycle(): void {
    if (!this.isConfigured) {
      return;
    }

    // Only re-run the JWT fetch (and boot/shutdown decision) when the user actually
    // changes — prevents refetching on every unrelated store emission. Compare ids
    // as strings since one emission may come from the persisted (sessionStorage)
    // snapshot and another from a fresh API response, which can differ in type
    // (e.g. number vs string) despite representing the same user.
    const userTrigger$ = this.store
      .select(AppSelectors.userDetails)
      .pipe(distinctUntilChanged((prevUser, user) => String(prevUser?.id) === String(user?.id)));

    this.subscriptions.add(
      userTrigger$
        .pipe(
          switchMap((user) => {
            // TEMP DEBUG: remove once the auth/intercom duplicate-call issue is confirmed fixed.
            console.debug('[IntercomService] userTrigger$ fired', {
              userId: user?.id,
              cachedForId: this.tokenRequestUserId,
              hasCachedRequest: !!this.tokenRequest$,
            });
            if (!user) {
              this.tokenRequestUserId = null;
              this.tokenRequest$ = null;
              return of({ user, intercomToken: null as { token: string } | null });
            }
            // Only request the JWT once we actually have a logged-in user — the
            // endpoint requires auth and we don't want to boot without a token.
            // Reuse the cached request/result for this user id so that repeated
            // `userDetails` emissions for the same user never re-fetch the token.
            if (String(this.tokenRequestUserId) !== String(user.id) || !this.tokenRequest$) {
              this.tokenRequestUserId = user.id;
              console.log('getting token');
              this.tokenRequest$ = this.authApiService.getIntercomToken().pipe(
                // Never let a failed token request kill this subscription permanently —
                // fall back to "no token" so future user changes can still retry.
                catchError((error) => {
                  console.error('IntercomService: failed to fetch Intercom JWT', error);
                  this.tokenRequestUserId = null;
                  this.tokenRequest$ = null;
                  return of(null as { token: string } | null);
                }),
                shareReplay(1),
              );
            }
            return this.tokenRequest$.pipe(map((intercomToken) => ({ user, intercomToken })));
          }),
          // Keep company attributes in sync whenever business details change, without
          // re-fetching the JWT or re-running the boot/shutdown logic above.
          switchMap((ctx) =>
            this.store.select(AppSelectors.businessDetails).pipe(map((business) => ({ ...ctx, business }))),
          ),
        )
        .subscribe(({ user, intercomToken, business }) => {
          if (!user) {
            this.shutdown();
            return;
          }

          // Without a JWT, Identity Verification will reject the boot — don't attempt it.
          if (!intercomToken?.token) {
            return;
          }

          const options: IntercomBootOptions = { intercomUserJwt: intercomToken.token };

          if (this.bootedUserId !== user.id) {
            this.bootUser(user, business, options);
            return;
          }

          this.update({ user, business });
        }),
    );
  }

  private buildSettings(user: UserProfile, business?: BusinessProfile | null, options: IntercomBootOptions = {}) {
    return {
      api_base: INTERCOM_API_BASE,
      app_id: this.appId,
      ...this.buildUserArgs(user),
      intercom_user_jwt: options.intercomUserJwt,
      hide_default_launcher: options.hideDefaultLauncher,
      company: business ? this.buildCompanyArgs(business) : undefined,
      background_color: '#5bbaf0', // secondary
      action_color: '#ff9688', // primary
      link_color: '#5bbaf0',
      theme_mode: 'light',
      alignment: 'right',
    } as IntercomSettings;
  }

  private buildUserArgs(user: UserProfile) {
    return {
      user_id: String(user.id),
      email: user.email,
      name: [user.first_name, user.last_name].filter(Boolean).join(' '),
      phone: user.phone || undefined,
    };
  }

  private buildCompanyArgs(business: BusinessProfile) {
    return {
      company_id: String(business.id),
      name: business.name,
      created_at: business.created ? Math.floor(new Date(business.created).getTime() / 1000) : undefined,
    };
  }
}
